<template>
  <el-dialog
    v-model="visible"
    width="500px"
    @close="onClose"
    :show-close="false"
    append-to-body
    destroy-on-close
    class="appointment-modal appointment-modal--view">
    <div class="appointment-modal-body">
    <button class="modal-close-btn" @click="onClose">×</button>
    <div class="appointment-header-visual">
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>{{ formatDate(appointment.date) }}</span>
          <span style="color: var(--ui-color-text-soft);">
            ({{ formattedDate(appointment.date) }}
            <span
              class="date-weekday"
              :class="{ weekend: isWeekend }"
            >
            {{ weekdayLabel }}
          </span>)
          </span>
        </div>
        <div class="header-item">
          <el-icon>
            <Clock />
          </el-icon>
          <span>{{ appointment.start_time }} – {{ appointment.end_time }}</span>
        </div>
      </div>
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>{{ appointment.user_name }}</span>
        </div>
        <div class="header-item">
          <el-icon>
            <Folder />
          </el-icon>
          <span>{{ appointment.room_name }}</span>
        </div>
      </div>
      <div class="header-row status-row">
        <div class="header-item">
          <el-icon>
            <AlarmClock />
          </el-icon>
          <span>{{ appointment.duration_time || totalDuration }} мин</span>
        </div>
      </div>
    </div>

    <div v-if="!clientIsDeleted" class="client-info-card">
      <div class="client-phone" v-if="appointment.client_phone">
        <el-icon>
          <Phone />
        </el-icon>
        <a :href="`tel:${appointment.client_phone}`" class="phone-link">{{ appointment.client_phone }}</a>
        <el-tooltip content="Скопировать" placement="top" popper-class="calendar-copy-tooltip">
          <el-button :icon="CopyDocument" text @click="copyPhone" class="copy-button" circle />
        </el-tooltip>
      </div>
      <div class="client-name" @click="showClientProfile" title="Показать профиль клиента">
        <el-icon>
          <UserFilled />
        </el-icon>
        <span class="name-text">{{ appointment.client_name }}</span>
        <el-button :icon="Edit" text circle @click.stop="showClientProfile" class="edit-button"
                   title="Редактировать клиента" />
      </div>
    </div>
    <div v-else class="client-info-card client-deleted">
      <el-icon>
        <UserFilled />
      </el-icon>
      <span style="color:#aaa;font-size:15px;margin-left:4px;">Клиент удалён или не найден</span>
    </div>

    <el-table
      v-if="appointment.services.length"
      class="services-table"
      :data="appointment.services"
      size="small"
      show-overflow-tooltip
    >
      <el-table-column prop="name" label="Услуга" />
      <el-table-column prop="duration" label="Минуты" width="110" />
      <el-table-column prop="price" label="Сумма" width="130" />
    </el-table>
    <div v-if="appointment.services.length" class="services-total">
      <span>Общая сумма:</span>
      <b>{{ appointment.total_sum }} ₽</b>
    </div>

    <div class="appointment-view-content">
      <div class="appointment-view-row" v-if="appointment.comment">
        <el-icon>
          <Comment />
        </el-icon>
        <span style="margin-left: 10px">{{ appointment.comment }}</span>
      </div>
    </div>
    </div>
    <template #footer>
      <div class="footer-bar">
        <div class="footer-status">
          <el-dropdown @command="changeStatus" trigger="click" popper-class="appointment-status-dropdown" v-if="hasPermission('appointments.edit')">
            <span class="status-trigger" :style="{ color: STATUS_MAP[appointment.status]?.color }">
              <el-icon class="status-icon"><Clock /></el-icon>
              <span class="status-label">{{ STATUS_MAP[appointment.status]?.label || appointment.status }}</span>
              <el-icon class="status-arrow"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="(obj, key) in STATUS_MAP"
                  :key="key"
                  :command="key"
                  :disabled="appointment.status === key"
                  :style="{ color: obj.color }"
                >
                  {{ obj.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <div class="footer-actions">
          <template v-if="appointment.status === 'done'">
            <el-button class="footer-main-btn" @click="cancelCompletion">Отменить выполнение</el-button>
          </template>
          <template v-else>
            <el-button
              v-if="hasPermission('appointments.edit')"
              class="footer-icon-btn footer-icon-btn--edit"
              circle
              @click="onEdit"
              title="Редактировать">
              <el-icon><Edit /></el-icon>
            </el-button>
            <el-button
              v-if="hasPermission('appointments.delete')"
              class="footer-icon-btn footer-icon-btn--delete"
              circle
              @click="onDelete"
              title="Удалить">
              <el-icon><Delete /></el-icon>
            </el-button>
            <el-button
              v-if="hasPermission('appointments.complete') && !['no_show', 'done', 'canceled'].includes(appointment.status)"
              class="footer-main-btn"
              @click="openClosed">
              Выполнить
            </el-button>
          </template>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import {
  AlarmClock,
  ArrowDown,
  Calendar,
  Clock,
  Comment,
  Files,
  Delete,
  Edit,
  Folder,
  Phone,
  UserFilled
} from '@element-plus/icons-vue'
import { mapState } from 'vuex'
import { hasPermission } from '@/components/util/accessHelper'

export default {
  name: 'AppointmentViewModal',
  components: {
    Delete,
    Comment,
    AlarmClock,
    Clock,
    Folder,
    Calendar,
    UserFilled,
    Phone,
    Edit,
    ArrowDown
  },
  props: {
    date: { type: String, required: true },
    appointment: { type: Object, required: true }
  },
  data() {
    return {
      showCompleteModal: false,
      visible: true,
      STATUS_MAP: {
        planned: { label: 'Запланировано', color: '#4fc5ff', icon: ['fas', 'clock'] },
        confirmed: { label: 'Подтверждено', color: '#4fc5ff', icon: ['fas', 'circle-check'] },
        no_show: { label: 'Не пришёл', color: '#ff7d7d', icon: ['fas', 'ban'] },
        canceled: { label: 'Отменено', color: '#8ca0c5', icon: ['fas', 'trash-can'] }
      }
    }
  },
  computed: {
    ...mapState('custom_fields', ['fields']),
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    CopyDocument() {
      return Files
    },
    Edit() {
      return Edit
    },
    clientIsDeleted() {
      // Если нет имени и нет телефона — считаем "удалён"
      const name = (this.appointment.client_name || '').trim()
      const phone = (this.appointment.client_phone || '').trim()
      // id может быть, но если имя и телефон пусты — это "удалён"
      return !name && !phone
    },
    totalDuration() {
      // если нет appointment.duration_time, считаем по услугам
      if (this.appointment && this.appointment.services && this.appointment.services.length) {
        return this.appointment.services.reduce((sum, s) => sum + (s.duration || 0), 0)
      }
      return 0
    }
  },
  methods: {
    hasPermission,
    formattedDate(date) {
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      // YYYY-MM-DD
      const [, month, day] = date.split('-')
      const dayStr = day.length === 1 ? `0${day}` : day
      const monthName = months[parseInt(month, 10) - 1]
      return `${dayStr} ${monthName}`
    },
    formatDate(date) {
      if (!date) return ''
      // формат "2025-06-06" → "06.06.2025"
      const parts = date.split('-')
      if (parts.length !== 3) return date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    onClose() {
      this.visible = false
      this.$emit('close')
    },
    onEdit() {
      this.$emit('edit', this.appointment)
    },
    showClientProfile() {
      if (this.appointment.client_id) {
        this.$emit('show-client', this.appointment.client_id)
      }
    },
    copyPhone() {
      if (this.appointment.client_phone) {
        navigator.clipboard.writeText(this.appointment.client_phone).then(() => {
          this.$message.success('Скопировано')
        }).catch(() => {
          alert('Не удалось скопировать')
        })
      }
    },
    changeStatus(newStatus) {
      this.$confirm(`Вы уверены, что хотите сменить статус на "${this.STATUS_MAP[newStatus].label}"?`, 'Смена статуса', {
        confirmButtonText: 'Да',
        cancelButtonText: 'Отмена',
        type: 'warning'
      }).then(() => {
        // Здесь вызов API на обновление статуса
        // this.updateStatus(newStatus)
        const data = {
          id: this.appointment.id,
          status: newStatus,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        };
        this.$store.dispatch('appointments/statusAppointment', data);
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = newStatus;
        this.$message.success('Статус обновлен')
      }).catch(() => {
        // Просто игнорируем отказ — пользователь нажал "Отмена"
      })
    },
    openClosed() {
      this.$emit('close')           // Закрыть предпросмотр
      this.$emit('open-complete')   // Сообщить родителю открыть "Выполнить"
    },
    async cancelCompletion() {
      try {
        await this.$confirm('Отменить выполнение и вернуть запись в активные?', 'Подтверждение', {
          confirmButtonText: 'Да',
          cancelButtonText: 'Отмена',
          type: 'warning'
        })
        await this.$store.dispatch('appointments/cancelCompletion', {
          appointment_id: this.appointment.id,
          account_id: this.appointment.account_id,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        })
        // --- Добавлено для реактивного обновления ---
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = 'planned'
        this.$emit('refresh-appointments')
        this.$message.success('Выполнение отменено, средства возвращены')
      } catch (err) {
        // отмена пользователем
      }
    },
    onDelete() {
      this.$confirm('Вы уверены, что хотите удалить эту запись?', 'Подтверждение удаления', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
        customClass: 'appointment-delete-confirm'
      }).then(() => {
        // Отправляем запрос в store
        this.$store.dispatch('appointments/deleteAppointment', {
          id: this.appointment.id,
          date: this.appointment.date,
          city_id: this.appointment.city_id,
          branch_id: this.appointment.branch_id
        })
          .then(() => {
            this.$message.success('Запись удалена')
            this.$emit('deleted', this.appointment.id)
            this.onClose()
          })
          .catch(() => {
            this.$message.error('Ошибка при удалении записи')
          })
      }).catch(() => {
        // Пользователь отменил удаление
      })
    },
  }
}
</script>

<style scoped>
.date-weekday {
  font-size: 15px;
  color: #7ab6ff;
  margin-bottom: 3px;
  font-weight: 700;
}
.date-weekday.weekend {
  color: #ff8f8f;
  font-weight: 700;
}
.services-total {
  margin-top: 8px;
  font-size: 16px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.services-total b {
  font-size: 16px;
  color: #57c7ff;
}

.services-table {
  margin-top: 8px;
  border-radius: 5px;
  overflow: hidden;
  --el-bg-color: transparent;
  --el-fill-color-blank: transparent;
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: transparent;
  --el-table-border-color: #031230;
  --el-table-row-hover-bg-color: rgba(24, 53, 104, 0.55);
  background:
    linear-gradient(rgb(16 53 117 / 49%), rgb(16 53 117 / 49%)),
    linear-gradient(151deg, #15264e 0%, #030b1b 75%);
  border: none;
}

.services-table :deep(.el-table__header-wrapper),
.services-table :deep(.el-table__body-wrapper),
.services-table :deep(.el-scrollbar__view),
.services-table :deep(.el-table__inner-wrapper) {
  background: transparent !important;
}

.services-table :deep(.el-table__inner-wrapper)::before,
.services-table :deep(.el-table__border-left-patch) {
  display: none !important;
}

.services-table :deep(th.el-table__cell),
.services-table :deep(td.el-table__cell) {
  background: transparent !important;
  border-right: 1px solid #031230 !important;
  border-bottom: 1px solid #031230 !important;
}

.services-table :deep(th.el-table__cell) {
  color: #e6efff !important;
  font-weight: 700;
  font-size: 13px;
}

.services-table :deep(td.el-table__cell) {
  color: #57c7ff !important;
  font-size: 13px;
}

.services-table :deep(.el-table__cell) {
  padding: 5px 0;
}

.services-table :deep(tr th.el-table__cell:last-child),
.services-table :deep(tr td.el-table__cell:last-child) {
  border-right: none !important;
}

.services-table :deep(tbody tr:last-child td.el-table__cell) {
  border-bottom: none !important;
}

.services-table :deep(th.el-table__cell:nth-child(2)),
.services-table :deep(th.el-table__cell:nth-child(3)),
.services-table :deep(td.el-table__cell:nth-child(2)),
.services-table :deep(td.el-table__cell:nth-child(3)) {
  text-align: center;
}

.appointment-header-visual {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.header-row {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #dce8ff;
  gap: 8px;
}

.status-row {
  margin-top: 4px;
}

.status-row .header-item {
  color: #dce8ff;
}

.status-row .header-item > span {
  color: #57c7ff;
}

.header-row:nth-child(2) .header-item:last-child > span {
  color: #57c7ff;
}

.status-item {
  gap: 6px;
  font-size: 14px;
  color: var(--ui-color-text-body);
  align-items: center;
}

.status-circle {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 4px;
  margin-right: 4px;
}

.status-gray {
  background-color: #999999;
}

.status-blue {
  background-color: var(--ui-color-brand);
}

.status-green {
  background-color: var(--ui-color-success);
}

.status-red {
  background-color: var(--ui-color-danger-el);
}

.client-info-card {
  border: none;
  border-radius: 5px;
  padding: 12px 16px;
  margin-bottom: 10px;
  background:
    linear-gradient(rgb(16 53 117 / 49%), rgb(16 53 117 / 49%)),
    linear-gradient(151deg, #15264e 0%, #030b1b 75%);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.client-phone {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: var(--ui-color-text-regular);
}

.phone-link {
  color: #dce8ff;
  text-decoration: none;
}

.phone-link:hover {
  text-decoration: underline;
}

.copy-button {
  padding: 0;
  margin-left: 4px;
  color: #7a95c4;
}

:deep(.copy-button.el-button.is-text),
:deep(.copy-button.el-button.is-text:hover),
:deep(.copy-button.el-button.is-text:focus),
:deep(.copy-button.el-button.is-text:active) {
  color: #031230 !important;
}

:deep(.copy-button.el-button.is-text:hover),
:deep(.copy-button.el-button.is-text:focus),
:deep(.copy-button.el-button.is-text:active) {
  background: linear-gradient(rgb(16 53 117 / 52%), rgb(16 53 117 / 52%)) !important;
  box-shadow: 0 0 0 1px rgb(63 111 184 / 55%) inset;
}

:deep(.calendar-copy-tooltip.el-popper) {
  background: linear-gradient(rgb(16 53 117 / 88%), rgb(16 53 117 / 88%)) !important;
  border: 1px solid #3f6fb8 !important;
  color: #dce8ff !important;
}

:deep(.calendar-copy-tooltip.el-popper .el-popper__arrow::before) {
  background: linear-gradient(rgb(16 53 117 / 88%), rgb(16 53 117 / 88%)) !important;
  border: 1px solid #3f6fb8 !important;
}

.client-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #dce8ff;
  cursor: pointer;
  user-select: none;
}

.name-text {
  user-select: text;
}

.edit-button {
  padding: 0;
  color: #7a95c4;
}

.edit-button:hover {
  color: #57c7ff;
}

.appointment-view-content {
  padding: 8px 0 0;
}

.appointment-view-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.view-label {
  min-width: 110px;
  color: var(--ui-color-text-soft);
  font-weight: 500;
  font-size: 15px;
}

.service-list {
  padding-left: 18px;
  margin: 0;
}

.client-deleted {
  color: #9cb3da;
  justify-content: flex-start;
  align-items: center;
  min-height: 34px;
  font-style: italic;
}
.footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
}

.footer-status {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 0 0 2px;
}

.status-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  max-width: 100%;
  color: #4ec7ff !important;
}

.status-icon {
  font-size: 16px;
  color: #04173e;
  flex: 0 0 auto;
}

.status-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-arrow {
  font-size: 14px;
  flex: 0 0 auto;
}

.footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
  padding: 0 2px 0 0;
}

.footer-actions :deep(.el-button) {
  margin: 0 !important;
}

.footer-actions :deep(.el-button + .el-button) {
  margin-left: 0 !important;
}

.footer-icon-btn {
  width: 34px !important;
  min-width: 34px !important;
  height: 34px !important;
  border: none !important;
  color: #0f1f49 !important;
}

.footer-icon-btn :deep(.el-icon) {
  font-size: 16px !important;
}

.footer-icon-btn--edit {
  background: linear-gradient(180deg, #486fbe 0%, #355ca9 100%) !important;
}

.footer-icon-btn--delete {
  background: linear-gradient(180deg, #081a45 0%, #051432 100%) !important;
  color: #4d69b5 !important;
}

.footer-icon-btn--delete :deep(.el-icon),
.footer-icon-btn--delete :deep(.el-icon svg),
.footer-icon-btn--delete :deep(.el-icon svg path) {
  color: #4d69b5 !important;
  fill: #4d69b5 !important;
  stroke: #4d69b5 !important;
}

.footer-main-btn {
  width: 118px !important;
  min-width: 118px !important;
  height: 34px !important;
  border: none !important;
  border-radius: 8px !important;
  background: linear-gradient(180deg, #6ed0ff 0%, #55bbee 100%) !important;
  color: #eef9ff !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  padding: 0 10px !important;
}

@media (max-width: 640px) {
  .footer-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .footer-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 1200px) {
  .status-trigger {
    font-size: 13px;
  }

  .status-icon {
    font-size: 15px;
  }

  .status-arrow {
    font-size: 13px;
  }

  .footer-icon-btn {
    width: 34px !important;
    min-width: 34px !important;
    height: 34px !important;
  }

  .footer-main-btn {
    width: 118px !important;
    min-width: 118px !important;
    height: 34px !important;
    font-size: 12px !important;
  }
}

.appointment-modal-body {
  background: linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)), linear-gradient(151deg, #15264e 0%, #030b1b 100%);
  border-radius: 8px;
  padding: 18px 20px 16px;
  position: relative;
}

:global(.appointment-modal) {
  background: transparent !important;
  border-radius: 8px;
  box-shadow: none !important;
  border: none !important;
  margin-top: 7vh !important;
}

:global(.appointment-modal .el-dialog__header) {
  background: transparent !important;
  padding: 0;
  margin: 0;
}

:global(.appointment-modal .el-dialog__body) {
  background: transparent !important;
  padding: 0;
}

:global(.appointment-modal .el-dialog__footer) {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

:global(.appointment-modal .el-dialog__headerbtn .el-dialog__close) {
  color: #0c1f49;
  font-size: 24px;
}

.modal-close-btn {
  position: absolute;
  right: 8px;
  top: 7px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #031230;
  font-size: 24px;
  line-height: 22px;
  cursor: pointer;
}

.appointment-modal-body :deep(.el-icon),
.appointment-modal-body :deep(.el-icon svg),
.appointment-modal-body :deep(.el-icon svg path) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

.header-item,
.appointment-view-row,
.services-total {
  color: #dce8ff;
}

.header-item :deep(.el-icon),
.client-phone :deep(.el-icon),
.client-name :deep(.el-icon),
.appointment-view-row :deep(.el-icon) {
  color: #031230 !important;
}

.header-item :deep(.el-icon svg),
.client-phone :deep(.el-icon svg),
.client-name :deep(.el-icon svg),
.appointment-view-row :deep(.el-icon svg) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

.copy-button :deep(.el-icon),
.edit-button :deep(.el-icon) {
  color: #031230 !important;
}

.copy-button :deep(.el-icon svg),
.edit-button :deep(.el-icon svg) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

:global(.appointment-modal .el-button),
:global(.appointment-modal .el-link),
:global(.appointment-modal .el-input__wrapper),
:global(.appointment-modal .el-textarea__inner),
:global(.appointment-modal .el-select__wrapper),
:global(.appointment-modal .el-input-number .el-input__wrapper) {
  transition: background-color .18s ease, color .18s ease, border-color .18s ease, box-shadow .18s ease, filter .18s ease;
}

:global(.appointment-modal .el-button:not(.el-button--primary):not(.el-button--success):not(.el-button--warning):not(.el-button--danger):hover),
:global(.appointment-modal .el-button:not(.el-button--primary):not(.el-button--success):not(.el-button--warning):not(.el-button--danger):focus-visible),
:global(.appointment-modal .el-button.is-text:hover),
:global(.appointment-modal .el-button.is-text:focus-visible) {
  background: linear-gradient(rgb(16 53 117 / 52%), rgb(16 53 117 / 52%)) !important;
  border-color: #3f6fb8 !important;
  color: #dce8ff !important;
  box-shadow: 0 0 0 1px rgb(63 111 184 / 55%) inset !important;
}

:global(.appointment-modal .el-button.el-button--primary:hover),
:global(.appointment-modal .el-button.el-button--success:hover),
:global(.appointment-modal .el-button.el-button--warning:hover),
:global(.appointment-modal .el-button.el-button--danger:hover),
:global(.appointment-modal .el-button.el-button--primary:focus-visible),
:global(.appointment-modal .el-button.el-button--success:focus-visible),
:global(.appointment-modal .el-button.el-button--warning:focus-visible),
:global(.appointment-modal .el-button.el-button--danger:focus-visible) {
  filter: brightness(.95);
}

:global(.appointment-modal .el-button:active) {
  filter: brightness(.9);
}

:global(.appointment-modal .el-link:hover),
:global(.appointment-modal .el-link:focus-visible) {
  color: #57c7ff !important;
}

:global(.appointment-modal .el-table__body tr:hover > td.el-table__cell) {
  background: rgba(24, 53, 104, 0.55) !important;
}

.client-phone,
.client-name,
.phone-link {
  color: #dce8ff;
}

</style>
